import { Injectable, inject, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { map, switchMap, tap, catchError, distinctUntilChanged } from 'rxjs/operators';
import { Auth, User, onAuthStateChanged, onIdTokenChanged, getAuth } from '@angular/fire/auth';
import { Firestore, doc, getDoc, setDoc, updateDoc } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

export interface UserState {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  role: string | null;
  organisationId: string | null;
  manufacturerId: string | null;
  loading: boolean;
}

const initialState: UserState = {
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  role: null,
  organisationId: null,
  manufacturerId: null,
  loading: true
};

@Injectable({
  providedIn: 'root'
})
export class UserStateService implements OnDestroy {
  private auth: Auth = inject(Auth);
  private firestore: Firestore = inject(Firestore);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private stateSubject = new BehaviorSubject<UserState>(initialState);
  private tokenRefreshTimer: any;
  private authInitialized = false;
  private authStateUnsubscribe: (() => void) | null = null;
  private tokenChangedUnsubscribe: (() => void) | null = null;
  
  // Collection path with environment
  private readonly usersCollection = `environments/${environment.environmentName}/users`;
  
  // Main state observable
  readonly state$ = this.stateSubject.asObservable();

  /** Synchronous snapshot for form scoping (guards still use state$). */
  get snapshot(): UserState {
    return this.stateSubject.value;
  }
  
  // Derived observables
  readonly user$ = this.state$.pipe(
    map(state => state.user),
    distinctUntilChanged()
  );
  
  readonly isAuthenticated$ = this.state$.pipe(
    map(state => state.isAuthenticated),
    distinctUntilChanged()
  );
  
  readonly isAdmin$ = this.state$.pipe(
    map(state => state.isAdmin),
    distinctUntilChanged()
  );
  
  readonly role$ = this.state$.pipe(
    map(state => state.role),
    distinctUntilChanged()
  );
  
  readonly organisationId$ = this.state$.pipe(
    map(state => state.organisationId),
    distinctUntilChanged()
  );

  readonly manufacturerId$ = this.state$.pipe(
    map(state => state.manufacturerId),
    distinctUntilChanged()
  );
  
  readonly loading$ = this.state$.pipe(map(state => state.loading));
  
  constructor() {
    console.log('UserStateService initialized');
    
    // Set loading state to true initially
    this.updateState({ loading: true });
    
    // Initialize auth listeners
    this.initAuth();
    
    // Check for existing authentication on startup
    this.checkExistingAuth();
  }
  
  private async checkExistingAuth(): Promise<void> {
    const currentUser = this.auth.currentUser;
    if (currentUser) {
      console.log('Found existing authenticated user on startup:', currentUser.email);
      try {
        await this.updateUserState(currentUser);
      } catch (error) {
        console.error('Error updating user state from existing auth:', error);
        this.updateState({ loading: false });
      }
    } else {
      console.log('No existing authenticated user found on startup');
      this.updateState({ loading: false });
    }
  }
  
  private async initAuth(): Promise<void> {
    if (this.authInitialized) return;
    
    try {
      // Initialize auth listeners without setting persistence
      this.initAuthListener();
      this.authInitialized = true;
      console.log('Firebase Auth listeners initialized');
    } catch (error) {
      console.error('Error initializing auth:', error);
      this.updateState({ loading: false });
    }
  }
  
  private initAuthListener(): void {
    // Clean up any existing listeners
    this.cleanupAuthListeners();
    
    // Listen for auth state changes (sign in/out)
    this.authStateUnsubscribe = onAuthStateChanged(this.auth, async (user) => {
      console.log('Auth state changed:', user ? `User: ${user.email}` : 'No user');
      
      // Set loading state to true when auth state changes
      this.updateState({ loading: true });
      
      if (user) {
        // User is signed in
        try {
          await this.updateUserState(user);
        } catch (error) {
          console.error('Error updating user state:', error);
          this.clearState();
          this.clearTokenRefreshTimer();
        }
      } else {
        // User is signed out
        this.clearState();
        this.clearTokenRefreshTimer();
      }
    }, (error) => {
      console.error('Auth state change error:', error);
      this.updateState({ loading: false });
    });
    
    // Also listen for token changes to catch admin claim updates
    this.tokenChangedUnsubscribe = onIdTokenChanged(this.auth, async (user) => {
      if (user) {
        console.log('ID token changed for user:', user.email);
        
        // Set loading state to true when token changes
        this.updateState({ loading: true });
        
        try {
          // Force token refresh to get latest claims
          const token = await user.getIdToken(true);
          console.log('Token refreshed, length:', token.length);
          await this.updateUserState(user);
        } catch (error) {
          console.error('Error handling token change:', error);
          this.updateState({ loading: false });
        }
      }
    }, (error) => {
      console.error('ID token change error:', error);
      this.updateState({ loading: false });
    });
  }
  
  private cleanupAuthListeners(): void {
    // Clean up existing listeners
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
      this.authStateUnsubscribe = null;
    }
    
    if (this.tokenChangedUnsubscribe) {
      this.tokenChangedUnsubscribe();
      this.tokenChangedUnsubscribe = null;
    }
  }
  
  // Update specific state properties
  private updateState(partialState: Partial<UserState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({ ...currentState, ...partialState });
  }
  
  // Set up a timer to refresh the token before it expires
  private setupTokenRefresh(expirationTime: string): void {
    this.clearTokenRefreshTimer();
    
    const expiresAt = new Date(expirationTime).getTime();
    const now = Date.now();
    
    // Refresh 5 minutes before expiration
    const refreshTime = expiresAt - now - (5 * 60 * 1000);
    
    if (refreshTime > 0) {
      console.log(`Setting up token refresh in ${Math.floor(refreshTime / 60000)} minutes`);
      console.log(`Token expires at: ${new Date(expiresAt).toLocaleString()}`);
      this.tokenRefreshTimer = setTimeout(() => this.refreshUserState(), refreshTime);
    } else {
      // Token is already expired or about to expire, refresh immediately
      console.log('Token is about to expire, refreshing immediately');
      this.refreshUserState();
    }
  }
  
  private clearTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }
  
  /**
   * Refreshes the user state based on the current authenticated user
   * @returns A promise that resolves when the user state has been refreshed
   */
  async refreshUserState(): Promise<void> {
    console.log('Manual refresh of user state requested');
    
    const currentUser = this.auth.currentUser;
    
    if (!currentUser) {
      console.log('No current user, resetting state');
      this.clearState();
      return Promise.resolve();
    }
    
    console.log('Refreshing state for user:', currentUser.email);
    this.updateState({ loading: true });
    
    try {
      // Get ID token result to check claims
      const idTokenResult = await currentUser.getIdTokenResult(true);
      
      // Log the claims for debugging
      console.log('ID Token claims:', idTokenResult.claims);
      
      // Try to get additional user data from Firestore
      try {
        const userDocRef = doc(this.firestore, `${this.usersCollection}/${currentUser.uid}`);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('User data from Firestore:', userData);
          
          // Update state with combined data
          this.updateState({
            user: currentUser,
            isAdmin: idTokenResult.claims['admin'] === true || userData['admin'] === true,
            role: userData['role'] as string || idTokenResult.claims['role'] as string || 'user',
            organisationId: userData['organisationId'] as string || idTokenResult.claims['organisationId'] as string || null,
            manufacturerId: (userData['manufacturerId'] as string) || null,
            loading: false
          });
        } else {
          console.log('No user document found in Firestore, using token claims only');
          this.updateState({
            user: currentUser,
            isAdmin: idTokenResult.claims['admin'] === true,
            role: idTokenResult.claims['role'] as string || 'user',
            organisationId: idTokenResult.claims['organisationId'] as string || null,
            manufacturerId: null,
            loading: false
          });
        }
      } catch (firestoreError) {
        console.error('Error accessing Firestore data:', firestoreError);
        // If Firestore fails, still update with token claims
        console.log('Falling back to token claims only due to Firestore error');
        this.updateState({
          user: currentUser,
          isAdmin: idTokenResult.claims['admin'] === true,
          role: idTokenResult.claims['role'] as string || 'user',
          organisationId: idTokenResult.claims['organisationId'] as string || null,
          manufacturerId: null,
          loading: false
        });
      }
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error refreshing user state:', error);
      
      // If there's an error, still update the state with the basic user info
      console.log('Falling back to basic authenticated state for user:', currentUser.email);
      this.updateState({
        user: currentUser,
        isAdmin: false,  // Default to non-admin on error
        role: 'user',    // Default role
        organisationId: null,
        manufacturerId: null,
        loading: false
      });
      
      return Promise.resolve();
    }
  }
  
  /**
   * Clears the user state, resetting to initial values
   * This is public so it can be called from AuthService during logout
   */
  public clearState(): void {
    console.log('Clearing user state');
    this.stateSubject.next({
      ...initialState,
      loading: false
    });
  }
  
  /**
   * Cleanup method to be called when the service is destroyed
   * This is important to prevent memory leaks
   */
  public cleanup(): void {
    this.cleanupAuthListeners();
    this.clearTokenRefreshTimer();
  }
  
  private async updateUserState(user: User): Promise<void> {
    try {
      console.log('Updating user state for:', user.email);
      
      // Get the user's custom claims (roles)
      const idTokenResult = await user.getIdTokenResult(true);
      const claims = idTokenResult.claims;
      
      console.log('Updating user state with claims:', claims);
      const isAdmin = !!claims['admin'];
      console.log('Admin status from claims:', isAdmin);
      
      // Get additional user data from Firestore
      const userDocRef = doc(this.firestore, `${this.usersCollection}/${user.uid}`);
      const userDoc = await getDoc(userDocRef);
      
      let organisationId: string | null = null;
      let manufacturerId: string | null = null;
      let role: string | null = null;
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('Retrieved user data from Firestore:', userData);
        
        organisationId = userData['organisationId'] || null;
        manufacturerId = userData['manufacturerId'] || null;
        role = userData['role'] || null;
        
        console.log(`User organization from Firestore: ${organisationId}`);
        console.log(`User manufacturer from Firestore: ${manufacturerId}`);
        console.log(`User role from Firestore: ${role}`);
        
        // Update last seen timestamp
        try {
          await updateDoc(userDocRef, {
            lastSeen: new Date()
          });
        } catch (updateError) {
          console.warn('Could not update lastSeen timestamp:', updateError);
          // Continue even if this fails
        }
      } else {
        console.log('No user document found in Firestore, creating one');
        
        // Create user document if it doesn't exist
        await setDoc(userDocRef, {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          createdAt: new Date(),
          lastLogin: new Date(),
          lastSeen: new Date()
        });
      }
      
      // Update the state
      this.stateSubject.next({
        user,
        isAuthenticated: true,
        isAdmin: isAdmin,
        role: role,
        organisationId: organisationId,
        manufacturerId: manufacturerId,
        loading: false
      });
      
      console.log('User state updated:', {
        isAdmin: isAdmin,
        role: role,
        organisationId: organisationId,
        manufacturerId: manufacturerId
      });
      
      // Set up token refresh
      this.setupTokenRefresh(idTokenResult.expirationTime);
    } catch (error) {
      console.error('Error getting user data:', error);
      
      // If we failed to get token claims or Firestore data, but we still have a user,
      // update the state with just the user to at least mark them as authenticated
      if (user) {
        console.log('Falling back to basic authenticated state for user:', user.email);
        this.stateSubject.next({
          user,
          isAuthenticated: true,
          isAdmin: false, // Default to non-admin if we can't verify
          role: null,
          organisationId: null,
          manufacturerId: null,
          loading: false
        });
      } else {
        // Make sure to set loading to false even if there's an error
        this.updateState({ loading: false });
      }
      
      throw error;
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
} 